"use client";

import { useCallback, useEffect, useState } from "react";
import { Navigate, useParams, useNavigate } from "react-router-dom";
import { NavigationTabs } from "@/components/common/navigationTabs";
import { TableContainer } from "@/components/common/TableContainer";
import { DataView } from "@/components/common/DataView";
import { ActionButton } from "@/components/common/DataViewTypes";
import { RefreshCw } from "lucide-react";
import { columns } from "./columns";
import { OrderSet } from "@/types/models/order";
import { getOrderSets } from "@/services/orderService";
import { usePermissions } from "@/hooks/usePermissions";

const OrderSetsIndexPage = () => {
  const { storeType, storeId } = useParams<{
    storeType: string;
    storeId: string;
  }>();
  const navigate = useNavigate();
  const { currentStore, isWholesaler } = usePermissions();
  const [pageIndex, setPageIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [orderSets, setOrderSets] = useState<OrderSet[]>([]);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 1,
  });

  const pageSize = 10;

  const fetchOrderSets = useCallback(async () => {
    if (!isWholesaler || !currentStore?.storeId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await getOrderSets({
        page: pageIndex + 1,
        limit: pageSize,
      });

      setOrderSets(response.data || []);
      setPagination(response.pagination);
    } catch (fetchError: any) {
      setError(fetchError?.message || "Failed to fetch order sets");
    } finally {
      setLoading(false);
    }
  }, [isWholesaler, currentStore?.storeId, pageIndex]);

  useEffect(() => {
    fetchOrderSets();
  }, [fetchOrderSets]);

  if (!isWholesaler && storeType && storeId) {
    return <Navigate to={`/${storeType}/${storeId}/orders`} replace />;
  }

  if (!isWholesaler) return null;

  const actionButtons: ActionButton[] = [
    {
      label: "Refresh",
      icon: (
        <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
      ),
      onClick: fetchOrderSets,
      variant: "outline",
      disabled: loading,
    },
  ];

  return (
    <div className="space-y-4">
      <TableContainer
        navigationTabs={
          <NavigationTabs
            tabs={[{ label: "Order Sets", isActive: true, onClick: () => {} }]}
            hasBackButton={false}
          />
        }
      >
        <div className="bg-secondary-background p-4.5 border rounded-2xl">
          <DataView
            columns={columns}
            data={orderSets}
            pagination={pagination}
            pageIndex={pageIndex}
            onPageChange={setPageIndex}
            loading={loading}
            error={error}
            title="Order Sets"
            description="Manage order sets for your wholesaler store"
            actionButtons={actionButtons}
            enableColumnResizing={true}
            enableRowSelection={false}
            emptyStateType="orders"
            loadingMessage="Loading order sets..."
            onRowClick={(row) =>
              navigate(`/${storeType}/${storeId}/order-sets/${row.id}`)
            }
          />
        </div>
      </TableContainer>
    </div>
  );
};

export default OrderSetsIndexPage;
